import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, BarChart3, Users } from 'lucide-react'
import { MessageBubble } from './MessageBubble'
import { ReplyBar } from './ReplyBar'
import { ConfirmSendDialog } from './ConfirmSendDialog'
import { broadcastQueryOptions, sendBroadcastFollowup } from '@/utils/broadcastQueryOptions'
import { useShellStore } from '@/stores/shellStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { shortTime } from '@/lib/format'
import { Skeleton } from '@/components/ui/skeleton'

export function BroadcastThread({ broadcastId }: { broadcastId: string }) {
  const isMobile = useIsMobile()
  const backToList = useShellStore((s) => s.backToList)
  const openDelivery = useShellStore((s) => s.openDelivery)
  const queryClient = useQueryClient()

  const [reply, setReply] = useState('')
  const [confirming, setConfirming] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  const { data, status, error } = useQuery(broadcastQueryOptions(broadcastId))

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data?.messages])

  const followupMutation = useMutation({
    mutationFn: () => sendBroadcastFollowup(broadcastId, reply.trim()),
    onSuccess: () => {
      setReply('')
      queryClient.invalidateQueries({ queryKey: ['broadcast', broadcastId] })
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] })
    },
  })

  const submitReply = () => {
    if (reply.trim() && !followupMutation.isPending) setConfirming(true)
  }

  if (status === 'pending') {
    return (
      <div className="flex h-full flex-col p-6">
        <Skeleton className="mb-4 h-8 w-48" />
        <Skeleton className="h-full w-full" />
      </div>
    )
  }

  if (status === 'error' || !data) {
    return (
      <div className="p-6 text-[13px] text-destructive">
        {error?.message || 'Broadcast not found'}
      </div>
    )
  }

  const everyone = `${data.textableRecipients} ${data.textableRecipients === 1 ? 'person' : 'people'}`
  const sentBy = data.senderName ? `Sent by ${data.senderName} · ${shortTime(data.createdAt)} · ` : ''
  const sub = `${sentBy}${data.totalRecipients} ${data.totalRecipients === 1 ? 'person' : 'people'} · ${data.textableRecipients} textable`

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex min-h-[56px] flex-none items-center justify-between gap-2.5 border-b border-border px-[18px] py-[9px]">
        <div className="flex min-w-0 items-center gap-2.5">
          {isMobile && (
            <button type="button" onClick={backToList} className="px-0.5 text-muted-foreground" aria-label="Back">
              <ChevronLeft className="h-[22px] w-[22px]" />
            </button>
          )}
          <div className="min-w-0">
            <div className="truncate text-base font-extrabold">{data.name || 'Broadcast'}</div>
            <div className="mt-px truncate text-[12px] text-muted-foreground">{sub}</div>
          </div>
        </div>
        <div className="flex flex-none items-center gap-2">
          <button
            type="button"
            onClick={openDelivery}
            className="inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-surface-2 px-3 py-2 text-[12.5px] font-semibold text-foreground"
          >
            <BarChart3 className="h-4 w-4" />
            Delivery
          </button>
          <button
            type="button"
            onClick={openDelivery}
            aria-label="Recipients"
            className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-border bg-surface-2 text-foreground"
          >
            <Users className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-1 flex-col gap-[11px] overflow-y-auto bg-chat-bg p-[18px]">
        {data.messages.map((m) => {
          const me = m.direction === 'outbound'
          const receipt =
            m.delivered != null
              ? { delivered: m.delivered, pending: m.pending ?? 0, failed: m.failed ?? 0 }
              : undefined
          return (
            <MessageBubble
              key={m.id}
              me={me}
              author={!me ? m.author || m.authorPhone : undefined}
              body={m.body}
              receipt={receipt}
            />
          )
        })}
        <div ref={endRef} />
      </div>

      {followupMutation.isError && (
        <div className="flex-none bg-destructive/10 px-4 py-2 text-[13px] text-destructive">
          {followupMutation.error.message}
        </div>
      )}

      {/* Looks like a chat box but texts everyone, so it says so and confirms. */}
      <ReplyBar
        value={reply}
        onChange={setReply}
        onSubmit={submitReply}
        disabled={followupMutation.isPending}
        placeholder={`Follow-up to ${everyone}…`}
        notice={`This is not a private reply: it texts all ${everyone}.`}
      />

      <ConfirmSendDialog
        open={confirming}
        onOpenChange={setConfirming}
        count={data.textableRecipients}
        message={reply.trim()}
        title="Send this follow-up to everyone?"
        onConfirm={() => followupMutation.mutate()}
      />
    </div>
  )
}
